import { Add, Delete } from "@mui/icons-material";
import {
    Box, Breadcrumbs,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Grid,
    IconButton, Menu, MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../api';
import CombinedPaymentsEditor from "../../components/checkout/CombinedPaymentsEditor";
import SalesChannelSelect from "../../components/checkout/SalesChannelSelect";
import ShippingMethodsSelect from "../../components/checkout/ShippingMethodsSelect";
import DidNotLoad from "../../components/DidNotLoad";
import Loading from "../../components/Loading";
import MoneyInput from "../../components/MoneyInput";
import MoneyInputWithBlur from "../../components/MoneyInputWithBlur";
import PhoneInput from "../../components/PhoneInput";
import {
    aggregatePaymentDateFromRows,
    buildPaymentMethodsPayload,
    createPaymentRow,
    paymentsFromApiMethods,
    syncSinglePaymentAmount,
    validatePaymentRows,
} from "../../helpers/checkoutPayments";
import { formatCurrencyInCents, formatPhone, resolvePublicMediaUrl } from "../../helpers/formatters";

function EditCheckoutPage() {
    const { id } = useParams(); // Pega o ID do checkout pela URL
    const [salesChannel, setSalesChannel] = useState('');
    const [customerId, setCustomerId] = useState('');
    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');
    const [productName, setProductName] = useState('');
    const [productUnitPrice, setProductUnitPrice] = useState('');
    const [productQuantity, setProductQuantity] = useState(1);
    const [productId, setProductId] = useState('');
    const [payments, setPayments] = useState(() => [createPaymentRow()]);
    const [customerWillPayLater, setCustomerWillPayLater] = useState(false);
    const [shippingMethod, setShippingMethod] = useState('');
    const [shippingCost, setShippingCost] = useState('0');
    const [shippingDeadline, setShippingDeadline] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [products, setProducts] = useState([]);
    const [amount, setAmount] = useState(0);
    const [receivedAmount, setReceivedAmount] = useState(0);
    const [finishOrder, setFinishOrder] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [didNotLoad, setDidNotLoad] = useState(false);
    let responseReceived = false;
    const navigate = useNavigate();
    const [resultErrors, setResultErrors] = useState({
        has_errors: false,
        customer_name: null,
        sales_channel: null,
        products: null,
        shipping_method: null,
        payment_methods: null,
    });
    const [isLoadingCustomerSearch, setIsLoadingCustomerSearch] = useState(false);
    const timerRef = useRef(null); // Usado para armazenar o timer
    const [customerResults, setCustomerResults] = useState([]);
    const [anchorElCustomer, setAnchorElCustomer] = useState(null);
    const [isLoadingProductSearch, setIsLoadingProductSearch] = useState(false);
    const [productResults, setProductResults] = useState([]);
    const [anchorElProduct, setAnchorElProduct] = useState(null);
    const [addProductErrors, setAddProductErrors] = useState({
        product_name: '',
        product_price: '',
        product_quantity: '',
    });
    const [checkoutImage, setCheckoutImage] = useState(null);
    const [useQuickCheckoutCreationDate, setUseQuickCheckoutCreationDate] = useState(false);
    /** Vindo da API — quick checkout não exige linhas de produto na edição. */
    const [isQuickCheckout, setIsQuickCheckout] = useState(false);

    useEffect(() => {
        if (!id) {
            return;
        }
        setIsLoading(true);
        setDidNotLoad(false);
        api.get(`checkouts/${id}`)
                    .then((response) => {
                        const data = response.data.data;
                        console.log(data);
                        setIsQuickCheckout(Boolean(data.is_quick_checkout));
                        setUseQuickCheckoutCreationDate(false);
                        setSalesChannel(data.sales_channel?.id || data.sales_channel?.uuid || '');
                        setCustomerName(data.customer?.name || '');
                        setCustomerPhone(data.customer?.phone || '');
                        setCustomerEmail(data.customer?.email || '');
                        setCustomerId(data.customer?.uuid || data.customer?.id || '');
                        if (data.products && data.products.length > 0) {
                            const updatedProducts = data.products.map((product) => {
                                const total = product.base_price * product.quantity;
                                return { ...product, total };
                            });
                            setProducts(updatedProducts || []);
                        } else {
                            setProducts([]);
                        }
                        try {
                            const fallbackDate = data.payment_details?.payment_date
                                ? new Date(data.payment_details.payment_date)
                                : new Date();
                            setPayments(paymentsFromApiMethods(data.payment_methods, {
                                totalAmountCents: data.amount || 0,
                                fallbackDate,
                            }));
                        } catch (ex) {
                            console.log(ex);
                            setPayments([createPaymentRow({ amountCents: data.amount || 0 })]);
                        }

                        setShippingMethod(data.shipping_method?.name || '');
                        try {
                            let currentShippingCost = data.shipping_method?.cost / 100;
                            if (isNaN(currentShippingCost)) {
                                currentShippingCost = 0;
                            }
                            setShippingCost(currentShippingCost.toString());
                        } catch (ex) {
                            setShippingCost('0');
                        }
                        setShippingDeadline(data.shipping_method?.deadline || '');
                        setAmount(data.amount || 0);
                        setReceivedAmount(data.amount || 0);

                        let payLater = !data.payment_details.paid;
                        if (data.photo != null) {
                            payLater = false;
                        }
                        setCustomerWillPayLater(payLater);
                        if (payLater) {
                            setPayments([]);
                        }

                        // Caminho /images/... é relativo ao host da API; sem resolver, o <img> aponta para o dev server do React.
                        const rawPhoto = data.photo || data.photo_url || data.image || data.image_url || null;
                        setCheckoutImage(rawPhoto ? resolvePublicMediaUrl(rawPhoto) : null);
                        setUseQuickCheckoutCreationDate(true);
                    })
                    .catch((error) => {
                        console.log(error);
                        setDidNotLoad(true);
                    })
                    .finally(() => {
                        setIsLoading(false);
                    });
    }, [id, navigate]);

    const handleCheckboxChange = (event) => {
        setCustomerWillPayLater(event.target.checked);
        if (event.target.checked) {
            setPayments([]);
        } else {
            setPayments([createPaymentRow({ amountCents: amount, paymentDate: new Date() })]);
        }
    };

    useEffect(() => {
        if (customerWillPayLater || isLoading) {
            return;
        }
        setPayments((prev) => syncSinglePaymentAmount(prev, amount));
    }, [amount, customerWillPayLater, isLoading]);

    const handleUpdateAmount = () => {
        let shippingCostInCents = 0;
        if (typeof shippingCost === 'string') {
            shippingCostInCents = parseFloat(shippingCost.replace(/[^\d]/g, ""));
        } else if (typeof shippingCost === 'number') {
            shippingCostInCents = shippingCost;
        }
        if (isNaN(shippingCostInCents)) {
            shippingCostInCents = 0;
        }
        const newAmount = products.reduce((sum, product) => sum + product.total, 0) + shippingCostInCents;
        setAmount(newAmount);
    };

    const handleAddProduct = () => {
        const unitPriceInCents = parseFloat(productUnitPrice.replace(/[^\d]/g, ""));
        let shippingCostInCents = 0;
        if (typeof shippingCost === 'string') {
            shippingCostInCents = parseFloat(shippingCost.replace(/[^\d]/g, ""));
        } else if (typeof shippingCost === 'number') {
            shippingCostInCents = shippingCost;
        }
        if (isNaN(shippingCostInCents)) {
            shippingCostInCents = 0;
        }
        const currentQuantity = parseInt(productQuantity);

        let hasProductErrors = false;
        let currentProductErrors = {
            product_name: '',
            product_price: '',
            product_quantity: '',
        };

        console.log('productId', productId);
        console.log('productName', productName);
        if (productName === '' && productId === '') {
            console.log('travando no product_name');
            currentProductErrors.product_name = 'Informe o nome do produto';
            hasProductErrors = true;
        }

        console.log('unitPriceInCents', unitPriceInCents);
        if (isNaN(unitPriceInCents) || unitPriceInCents <= 0) {
            console.log('travando no product_price');
            currentProductErrors.product_price = 'Informe o valor do produto';
            hasProductErrors = true;
        }

        console.log('currentQuantity', currentQuantity);
        if (isNaN(currentQuantity) || currentQuantity <= 0) {
            console.log('travando no product_quantity');
            currentProductErrors.product_quantity = 'Deve ser informada a quantidade';
            hasProductErrors = true;
        }

        console.log('hasProductErrors', hasProductErrors);
        console.log('addProductErrors', addProductErrors);

        if (hasProductErrors) {
            setResultErrors({
                ...resultErrors,
                has_errors: true,
            });
            setAddProductErrors({
                ...addProductErrors,
                ...currentProductErrors,
            })
            return
        }

        setAddProductErrors({
            product_name: '',
            product_price: '',
            product_quantity: '',
        });
        setResultErrors({
            ...resultErrors,
            has_errors: false,
        });
        hasProductErrors = false;

        let product = {
            name: productName,
            base_price: unitPriceInCents,
            quantity: currentQuantity,
            total: unitPriceInCents * productQuantity
        };
        if (productId !== '') {
            product.id = productId;
            setProductId('');
        }

        const newProducts = [...products, product];
        setProducts(newProducts);

        const newAmount = newProducts.reduce((sum, product) => sum + product.total, 0) + shippingCostInCents;
        setAmount(newAmount);

        setProductName('');
        setProductUnitPrice('');
        setProductQuantity(1);
    };

    const handleRemoveProduct = (index) => {
        let shippingCostInCents = 0;
        if (typeof shippingCost === 'string') {
            shippingCostInCents = parseFloat(shippingCost.replace(/[^\d]/g, ""));
        } else if (typeof shippingCost === 'number') {
            shippingCostInCents = shippingCost;
        }
        if (isNaN(shippingCostInCents)) {
            shippingCostInCents = 0;
        }
        const newProducts = products.filter((_, i) => i !== index);
        setProducts(newProducts);

        const newAmount = newProducts.reduce((sum, product) => sum + product.total, 0) + shippingCostInCents;
        setAmount(newAmount);
    };

    const handleSubmit = (event, options = {}) => {
        event.preventDefault();

        const finishOrderFlag = typeof options.finishOrder === 'boolean' ? options.finishOrder : finishOrder;

        let allRequiredFieldsOk = true;
        let invalidData = {};
        if (!isQuickCheckout && products.length === 0) {
            invalidData.products = 'Você deve incluir ao menos 1 produto. Informe o nome, valor unitário, quantidade e clique no botão "Incluir este produto" para adicionar.';
            allRequiredFieldsOk = false;
        }

        if (customerId === '' && customerName === '') {
            invalidData.customer_name = 'Informe o nome do cliente';
            allRequiredFieldsOk = false;
        }
        if (!customerWillPayLater) {
            const paymentError = validatePaymentRows(payments, amount);
            if (paymentError) {
                invalidData.payment_methods = paymentError;
                allRequiredFieldsOk = false;
            }
        }
        if (!allRequiredFieldsOk) {
            setResultErrors({
                ...resultErrors,
                ...invalidData,
                has_errors: true,
            });
            return
        }

        setIsSubmitting(true);

        const paymentMethods = customerWillPayLater ? [] : buildPaymentMethodsPayload(payments);
        const aggregatePaymentDate = customerWillPayLater
            ? null
            : aggregatePaymentDateFromRows(payments);

        const updatedCheckout = {
            customer: {
                id: customerId,
                name: customerName,
                phone: customerPhone,
                email: customerEmail,
            },
            shipping_method: {
                name: shippingMethod,
                cost: shippingCost * 100,
                deadline: parseInt(shippingDeadline),
            },
            products: products,
            amount: amount,
            sales_channel: {
                id: salesChannel,
            },
            finish_order: finishOrderFlag,
            payment_details: {
                paid: !customerWillPayLater,
            },
            use_quick_checkout_creation_date: useQuickCheckoutCreationDate,
        };
        if (paymentMethods.length > 0) {
            updatedCheckout.payment_methods = paymentMethods;
        }

        if (!customerWillPayLater && aggregatePaymentDate != null) {
            updatedCheckout.payment_details.payment_date = aggregatePaymentDate.toISOString();
        }

        let redirectTo = '/admin/checkouts'
        if (finishOrderFlag) {
            redirectTo = '/admin/orders'
        }

        api.put(`checkouts/${id}`, updatedCheckout)
            .then((response) => {
                responseReceived = true;
                try {
                    const data = response.data;
                    if (!data?.success) {
                        setResultErrors({
                            ...data.errors,
                            has_errors: true,
                        });
                    } else {
                        setFinishOrder(finishOrderFlag);
                        navigate(redirectTo);
                    }
                } catch (error) {
                    console.log(error);
                }
            })
            .catch((error) => {
                responseReceived = true;
                try {
                    const data = error.response.data;
                    setResultErrors({
                        ...data.errors,
                        has_errors: true,
                    });
                } catch (error) {
                    console.log(error);
                }
            })
            .finally(() => {
                setIsSubmitting(false);
                if (!responseReceived) {
                    setDidNotLoad(true);
                }
            });
    };

    const customerSearch = (customerName) => {
        if (customerName.length >= 3) {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }

            timerRef.current = setTimeout(() => {
                setIsLoadingCustomerSearch(true);
                api.get(`customers?q=${customerName}`)
                    .then((response) => {
                        const d = response.data;
                        const rows = Array.isArray(d?.customers)
                            ? d.customers
                            : Array.isArray(d?.data)
                                ? d.data
                                : [];
                        setCustomerResults(rows);

                        setIsLoadingCustomerSearch(false);
                    })
                    .catch((error) => {
                        console.error(error);
                        setCustomerResults([]); // Evita que o menu fique aberto com erro
                        setIsLoadingCustomerSearch(false);
                    });
            }, 1000);
        } else {
            setCustomerResults([]); // Limpa os resultados se o nome for menor que 3 caracteres
        }
    };

    const handleCloseCustomerMenu = () => {
        setAnchorElCustomer(null);
        setCustomerResults([]);
    };

    const productSearch = (productName) => {
        if (productName.length >= 3) {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }

            timerRef.current = setTimeout(() => {
                setIsLoadingProductSearch(true);
                api.get(`products?q=${productName}`)
                    .then((response) => {
                        const d = response.data;
                        const rows = Array.isArray(d?.products)
                            ? d.products
                            : Array.isArray(d?.data)
                                ? d.data
                                : [];
                        setProductResults(rows);

                        setIsLoadingProductSearch(false);
                    })
                    .catch((error) => {
                        console.error(error);
                        setProductResults([]); // Evita que o menu fique aberto com erro
                        setIsLoadingProductSearch(false);
                    });
            }, 1000);
        } else {
            setProductResults([]); // Limpa os resultados se o nome for menor que 3 caracteres
        }
    };

    const handleCloseProductMenu = () => {
        setAnchorElProduct(null);
        setProductResults([]);
    };

    if (isLoading) {
        return <Loading />;
    }

    if (didNotLoad) {
        return <DidNotLoad />
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link onClick={() => navigate('/admin')} to={'/admin'}>
                    Dashboard
                </Link>
                <Link onClick={() => navigate('/admin/checkouts')} to={'/admin/checkouts'}>
                    Checkouts
                </Link>
                <Typography color="text.primary">Editar venda</Typography>
            </Breadcrumbs>
            <br />
            <Typography variant="h4" gutterBottom>Editar Venda</Typography>
            <form onSubmit={(e) => handleSubmit(e)}>
                <Grid item xs={12} sm={12}>Canal de vendas</Grid>
                <Box mb={2} />
                <SalesChannelSelect
                    label="Por onde a venda está sendo feita"
                    value={salesChannel}
                    onChange={(e) => setSalesChannel(e.target.value)}
                />
                {resultErrors.sales_channel && (
                    <div
                        className="text-danger mt-1"
                        style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                    >
                        {resultErrors.sales_channel}
                    </div>
                )}
                <Box mb={6} />
                <Grid item xs={12} sm={12}>
                    <Typography variant="h5">Dados do cliente</Typography>
                </Grid>
                <Box mb={2} />
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            id="customer-name"
                            label={isLoadingCustomerSearch ? 'Buscando cliente...' : 'Nome (obrigatório)'}
                            fullWidth
                            value={customerName}
                            onChange={(e) => {
                                setCustomerName(e.target.value);
                                customerSearch(e.target.value);
                            }}
                            onFocus={(e) => setAnchorElCustomer(e.currentTarget)} // Aqui está a correção!
                        />
                        <Menu
                            anchorEl={anchorElCustomer}
                            open={Boolean(anchorElCustomer) && customerResults.length > 0}
                            onClose={handleCloseCustomerMenu} // Fecha o menu ao clicar fora
                            disableAutoFocusItem
                        >
                            {customerResults.map((customer) => (
                                <MenuItem
                                    key={customer.uuid || customer.id}
                                    onClick={() => {
                                        setCustomerName(customer.name);
                                        setCustomerEmail(customer.email || '');
                                        setCustomerPhone(formatPhone(customer.phone));
                                        setCustomerId(customer.uuid || customer.id || '');
                                        handleCloseCustomerMenu();
                                    }}
                                >
                                    {customer.name}
                                </MenuItem>
                            ))}
                        </Menu>
                        {resultErrors.customer_name && (
                            <div
                                className="text-danger mt-1"
                                style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                            >
                                {resultErrors.customer_name}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <PhoneInput
                            label="Telefone"
                            value={customerPhone}
                            onChange={(e) => setCustomerPhone(e.target.value)}
                            disabled={false}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="E-mail"
                            fullWidth
                            value={customerEmail}
                            onChange={(e) => setCustomerEmail(e.target.value)}
                            type="email"
                        />
                        {resultErrors.customer_email && (
                            <div
                                className="text-danger mt-1"
                                style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                            >
                                {resultErrors.customer_email}
                            </div>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={12}><hr /></Grid>

                    {/* Exibir imagem se existir */}
                    {checkoutImage && (
                        <Grid item xs={12} sm={12}>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                marginBottom: 3,
                                padding: 2,
                                // backgroundColor: '#f5f5f5',
                                borderRadius: 2,
                                border: '1px solid #bbb'
                            }}>
                                <Typography variant="h6" sx={{ marginBottom: 2, color: '#666' }}>
                                    Produtos nesta venda
                                </Typography>
                                <img
                                    src={checkoutImage}
                                    alt="Foto dos produtos do checkout"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '400px',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                        objectFit: 'contain'
                                    }}
                                />
                            </Box>
                        </Grid>
                    )}

                    <Grid item xs={12} sm={12}>
                        <Typography variant="h5">Produtos</Typography>
                    </Grid>
                    <Box mb={2} />
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label={isLoadingProductSearch ? 'Buscando produto...' : 'Nome do produto (obrigatório)'}
                            fullWidth
                            value={productName}
                            onChange={(e) => {
                                setProductName(e.target.value);
                                productSearch(e.target.value);
                            }}
                            onFocus={(e) => setAnchorElProduct(e.currentTarget)} // Aqui está a correção!
                        />
                        <Menu
                            anchorEl={anchorElProduct}
                            open={Boolean(anchorElProduct) && productResults.length > 0}
                            onClose={handleCloseProductMenu} // Fecha o menu ao clicar fora
                            disableAutoFocusItem
                        >
                            {productResults.map((product) => (
                                <MenuItem
                                    key={product.uuid || product.id}
                                    onClick={() => {
                                        setProductName(product.name);
                                        setProductUnitPrice((product.base_price / 100));
                                        setProductQuantity(1);
                                        setProductId(product.uuid || product.id);
                                        handleCloseProductMenu();
                                    }}
                                >
                                    {product.name} — {formatCurrencyInCents(product.base_price)}
                                </MenuItem>
                            ))}
                        </Menu>
                        {addProductErrors.product_name && (
                            <div
                                className="text-danger mt-1"
                                style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                            >
                                {addProductErrors.product_name}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <MoneyInput
                            label="Valor unitário"
                            value={productUnitPrice}
                            onChange={(newValue) => setProductUnitPrice(newValue)} // Passa apenas o valor numérico bruto
                        />
                        {addProductErrors.product_price && (
                            <div
                                className="text-danger mt-1"
                                style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                            >
                                {addProductErrors.product_price}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="Quantidade"
                            fullWidth
                            value={productQuantity}
                            onChange={(e) => setProductQuantity(e.target.value)}
                            inputMode="numeric"
                        />
                        {addProductErrors.product_quantity && (
                            <div
                                className="text-danger mt-1"
                                style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                            >
                                {addProductErrors.product_quantity}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleAddProduct}
                            startIcon={<Add />}
                            fullWidth
                        >
                            Incluir este produto
                        </Button>
                    </Grid>

                    <Grid item xs={12} sm={12}>Produtos Adicionados</Grid>
                    <Box mb={2} />
                    <Grid item xs={12} sm={12}>
                        <ul>
                            {products.map((product, index) => (
                                <li key={index}>
                                    {product.name} - {product.quantity} x {formatCurrencyInCents(product.base_price)} = {formatCurrencyInCents(product.total)}
                                    <IconButton onClick={() => handleRemoveProduct(index)}>
                                        <Delete color="error" />
                                    </IconButton>
                                </li>
                            ))}
                        </ul>
                        {resultErrors.products && (
                            <div
                                className="text-danger mt-1"
                                style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                            >
                                {resultErrors.products}
                            </div>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={12}><hr /></Grid>
                    <Grid item xs={12} sm={12}>
                        <Typography variant="h5">Dados de pagamento</Typography>
                    </Grid>
                    <Box mb={2} />

                    <Grid item xs={12} sm={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={customerWillPayLater}
                                    onChange={handleCheckboxChange}
                                    color="primary"
                                />
                            }
                            label="O pagamento desta venda será realizado em outro momento"
                        />
                    </Grid>
                    {!customerWillPayLater && (
                        <Grid item xs={12}>
                            <CombinedPaymentsEditor
                                payments={payments}
                                onChange={setPayments}
                                totalAmountCents={amount}
                                defaultDate={new Date()}
                            />
                        </Grid>
                    )}

                    {resultErrors.payment_methods && (
                        <Grid item xs={12} sm={12}>
                            <div
                                className="text-danger mt-1"
                                style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                            >
                                {resultErrors.payment_methods}
                            </div>
                        </Grid>
                    )}

                    <Grid item xs={12} sm={12}><hr /></Grid>
                    <Grid item xs={12} sm={12}>
                        <Typography variant="h5">Dados de entrega</Typography>
                    </Grid>
                    <Box mb={2} />
                    <Grid item xs={12} sm={4}>
                        <ShippingMethodsSelect
                            label="Forma de entrega"
                            value={shippingMethod}
                            onChange={(e) => setShippingMethod(e.target.value)}
                        />
                        {resultErrors.shipping_method && (
                            <div
                                className="text-danger mt-1"
                                style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                            >
                                {resultErrors.shipping_method}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <MoneyInputWithBlur
                            label="Informe o valor do frete"
                            value={shippingCost}
                            onChange={(values) => setShippingCost(values.value)}
                            onBlur={handleUpdateAmount}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Prazo em dias"
                            fullWidth
                            value={shippingDeadline}
                            onChange={(e) => setShippingDeadline(e.target.value)}
                            inputMode="numeric"
                        />
                    </Grid>

                    <Grid item xs={12} sm={12}><hr /></Grid>
                    <Grid item xs={12} sm={12}>
                        {checkoutImage && (
                            <Typography variant="h4" align="center">Valor total registrado pelo Quick Checkout: {formatCurrencyInCents(receivedAmount)}</Typography>
                        )}
                        <Typography variant="h3" align="center">Total: {formatCurrencyInCents(amount)}</Typography>
                    </Grid>
                </Grid>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' }, // Empilha no mobile, lado a lado em telas maiores
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: 2,
                        marginBottom: 8,
                        gap: 2 // Espaçamento entre os itens
                    }}
                >
                    {/* Mensagem de erro à esquerda */}
                    {resultErrors.has_errors ? (
                        <div
                            className="text-danger"
                            style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                        >
                            Ocorreram erros de validação, verifique os campos acima.
                        </div>
                    ) : (
                        <div style={{ height: '1rem' }} />
                    )}

                    {/* Botões responsivos */}
                    <Grid container spacing={2} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                        <Grid item xs={12} sm="auto">
                            <Button
                                variant="contained"
                                color="primary"
                                type="button"
                                disabled={isSubmitting}
                                startIcon={isSubmitting && <CircularProgress size={20} color="inherit" />}
                                onClick={(e) => handleSubmit(e, { finishOrder: false })}
                                fullWidth // Garante que o botão fique 100% em telas pequenas
                            >
                                {isSubmitting ? 'Cadastrando...' : 'Armazenar e seguir'}
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm="auto">
                            <Button
                                variant="contained"
                                color="secondary"
                                type="button"
                                disabled={isSubmitting}
                                startIcon={isSubmitting && <CircularProgress size={20} color="inherit" />}
                                onClick={(e) => handleSubmit(e, { finishOrder: true })}
                                fullWidth // Garante que o botão fique 100% em telas pequenas
                            >
                                {isSubmitting ? 'Concluindo venda...' : 'Concluir venda'}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </form>
        </div>
    );
}

export default EditCheckoutPage;
